import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, Share2, Brain, Copy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";
import BlueprintView from "../components/BlueprintView";

export default function Dashboard() {
  const { profile } = useAuth();
  const pid = profile?.id;
  const [data, setData] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [loadingSynth, setLoadingSynth] = useState(false);
  const [token, setToken] = useState(profile?.public_token || null);

  useEffect(() => {
    if (!pid) return;
    (async () => {
      const { data } = await api.get(`/profiles/${pid}/full`);
      setData(data);
      setToken(data.public_token);
      if (data.psychology) fetchSynthesis(data);
    })();
    // eslint-disable-next-line
  }, [pid]);

  const fetchSynthesis = async (d, regen = false) => {
    setLoadingSynth(true);
    try {
      const url = regen ? `/profiles/${pid}/synthesis/regenerate` : `/profiles/${pid}/synthesis`;
      const { data: s } = regen ? await api.post(url) : await api.get(url);
      setSynthesis(s.sections);
    } catch { toast.error("Could not generate synthesis"); } finally { setLoadingSynth(false); }
  };

  const share = async () => {
    const { data } = await api.post(`/profiles/${pid}/share`);
    setToken(data.public_token);
    const url = `${window.location.origin}/blueprint/${data.public_token}`;
    try {
      await navigator.clipboard?.writeText(url);
      toast.success("Public link copied to clipboard");
    } catch {
      toast.success("Public link ready", { description: url });
    }
  };

  if (profile === null) return <Navigate to="/setup" replace />;
  if (!data) return <Shell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div></Shell>;

  return (
    <Shell>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Your unified self-portrait</p>
          <h1 className="font-serif text-5xl">The Blueprint</h1>
        </div>
        <div className="flex gap-3">
          {!data.psychology && (
            <Link to="/assessment" data-testid="take-assessment-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/50 text-primary hover:bg-primary/10 transition-colors">
              <Brain size={16} /> Take assessment
            </Link>
          )}
          <button data-testid="share-btn" onClick={share} className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-border hover:border-primary/50 transition-colors">
            {token ? <Copy size={16} /> : <Share2 size={16} />} {token ? "Copy link" : "Share"}
          </button>
          {data.psychology && (
            <button data-testid="regen-synthesis-btn" onClick={() => fetchSynthesis(data, true)} disabled={loadingSynth}
              className="gold-btn flex items-center gap-2 px-5 py-2.5 rounded-full disabled:opacity-60">
              {loadingSynth ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Regenerate
            </button>
          )}
        </div>
      </div>

      {data.psychology && loadingSynth && !synthesis && (
        <div className="panel p-6 mb-4 flex items-center gap-3 text-muted-foreground text-sm">
          <Loader2 className="animate-spin text-primary" size={18} /> Weaving your systems into one portrait…
        </div>
      )}

      <BlueprintView data={data} synthesis={synthesis} profileId={pid}
        onRegenerate={() => fetchSynthesis(data, true)}
        onUpdate={async () => { const { data: d } = await api.get(`/profiles/${pid}/full`); setData(d); }} />
    </Shell>
  );
}
